import { IoMdClose } from "react-icons/io";
import { useCallback, useEffect, useState } from "react";
import {
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import dayjs from "dayjs";
import {
  ConfirmOrderBatchProductCreator,
  StockOrderHandleQuantity,
} from "../components";
import { OrderTableData } from "features/stock-ordering/core/domain/order-table-row.model";
import {
  getStockOrderStores,
  selectGetStockOrderStores,
} from "../slices/get-store.slice";
import {
  resetNewOrder,
  selectconfirmNewOrder,
} from "../slices/confirm-new-order.slice";
import { insertNewOrder } from "../slices/insert-new-order.slice";
import {
  MaterialDateInput,
  MaterialInput,
} from "features/shared/presentation/components";
import {
  categoryModel,
  fulfillmentModel,
} from "features/stock-ordering/core/domain/store-and-category.model";
import { createQueryParams } from "features/config/helpers";
import {
  openMessageModal,
  closeMessageModal,
} from "features/shared/presentation/slices/message-modal.slice";
import { selectGetMinimumTotalAmountData } from "../slices/get-minimum-total-amount.slice";
import {
  getAdminSession,
  selectGetAdminSession,
} from "features/admin/presentation/slices/get-admin-session.slice";
import { selectAdminSettingsEditUserMaterialSupplierCustomer } from "features/admin/presentation/slices/admin-settings-edit-user-meterial-supplier-customer.slice";
import { Column } from "features/shared/presentation/components/data-table";
import {
  BannerIndicator,
  dateSetup,
  NoticeBanner,
  moneyFormatter,
} from "../components/stock-ordering-utils";
import { IoArrowBackCircleOutline } from "react-icons/io5";
import {
  selectStockInventoryFetchCurrentStock,
  stockInventoryFetchCurrentStock,
} from "../slices/stock-inventory-fetch-current-stock.slice";
import { LuTrash2 } from "react-icons/lu";
import { AddProductSelectModal } from "./add-product-selection.modal";
import { selectGetStockOrderProducts } from "../slices/get-products.slice";
import { StockProductItem } from "features/stock-ordering/core/domain/get-stock-product.model";
import React from "react";
import { Package, Plus, Building, Calendar, Truck, Boxes } from "lucide-react";
import { BatchModel } from "features/stock-ordering/core/domain/batch.model";

interface ConfirmOrdersModalProps {
  open: boolean;
  onClose: () => void;
  isOrderAgain?: boolean;
  openPlaceOrderModal?: (value: boolean) => void;
}

export function ConfirmOrdersModal(props: ConfirmOrdersModalProps) {
  /**
   * Dispatch for fetching and pushing data
   */
  const dispatch = useAppDispatch();

  /**
   * selector that contains store informations
   */
  const getStores = useAppSelector(selectGetStockOrderStores);

  /**
   * Selector for fetching inputted order information
   */
  const getOrderInformation = useAppSelector(selectconfirmNewOrder);

  /**
   * Selector for current stock
   */
  const inventoryFechCurrentStockState = useAppSelector(
    selectStockInventoryFetchCurrentStock
  );

  /**
   * Selector for minimum total amount
   */
  const getMinimumTotalAmountState = useAppSelector(
    selectGetMinimumTotalAmountData
  );

  /**
   * Selector for supplier and customer data permissions
   */
  const adminSettingsEditUserMaterialSupplierCustomerState = useAppSelector(
    selectAdminSettingsEditUserMaterialSupplierCustomer
  );

  /**
   * Selector admin session data
   */
  const getAdminSessionState = useAppSelector(selectGetAdminSession);

  const getProductData = useAppSelector(selectGetStockOrderProducts);

  /**
   * Destructure order data
   */
  const { data } = getOrderInformation ?? { data: { category: null } };

  /**
   * Destructure inventory current stock
   */
  const { currentInventory } = inventoryFechCurrentStockState.data ?? {};

  /**
   * @constant totalAmount - state container for total amount
   * @constant deliveryDate - state container for delivery date
   * @constant isOrderOverride - state container for override
   * @constant isChecked - state that determines switch is enabled
   */
  const [totalAmount, setTotalAmount] = useState(0);
  const [deliveryDate, setDeliveryDate] = useState<string | null>(null);
  const [isOrderOverride, setIsOrderOverride] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [test, setTest] = useState(false);
  const [openAddProduct, setOpenAddProduct] = useState(false);
  const [batch, setBatches] = useState<BatchModel[]>([]);
  const [orderProductData, setOrderProductData] = useState<OrderTableData[]>(
    []
  );

  /**
   * Determines if the ordering is multim based on companyId
   */
  const isMultimOrdering =
    getAdminSessionState.data?.admin.user_details.company.company_id === 1;

  /**
   * Dynamic column
   */
  const columns: Array<Column> = [
    { id: "prodId", label: "Product ID" },
    { id: "prodName", label: "Product Name" },
    { id: "cost", label: "Unit Cost" },

    // Hide stock count on multim pov
    isMultimOrdering ? null : { id: "stock", label: "Available Stock" },
    { id: "orderQty", label: "Order Qty" },
    { id: "uom", label: "UOM" },

    { id: "itemTotalCost", label: "Total Cost" },
    { id: "trash", label: "" },
  ].filter((column): column is Column => column !== null);

  useEffect(() => {
    if (props.open && orderProductData.length === 0) {
      setOrderProductData(data?.OrderData ?? []);
    }
  }, [props.open]);

  /**
   * Updates totalAmount for every product data change
   */
  useEffect(() => {
    //Computes total amount
    const finalTotal =
      orderProductData?.reduce(
        (acc, prodData) => acc + Number(prodData.itemTotalCost),
        0
      ) ?? 0;

    setTotalAmount(finalTotal);

    // Dispatch stores if undefined
    if (!getStores.data) {
      const query = createQueryParams({
        store_id: getOrderInformation.data?.selectedStoreId ?? "",
      });

      dispatch(getStockOrderStores(query));
    }
  }, [orderProductData]);

  /**
   * Update confirmNewOrder for every change of quantity
   * @param prodIndex - specific index of the order product data
   */
  const handleProductChange = useCallback(
    (index: number, updatedProduct: OrderTableData) => {
      setOrderProductData((prev) => {
        const updated = [...prev];
        updated[index] = updatedProduct;
        return updated;
      });
    },
    []
  );

  /**
   * Handles requested delivery date change
   * @param date - selected date
   */
  const handleRequestedDeliveryDate = (
    date: string | number | Date | dayjs.Dayjs | null | undefined
  ) => {
    if (date) {
      const formattedDate = dayjs(date).format("YYYY-MM-DD");

      if (dayjs(formattedDate).isValid()) {
        setDeliveryDate(formattedDate);
      }
    }
  };

  const handleSubmit = () => {
    const withOutOfStock = orderProductData?.some((orderItem) => {
      if (
        getAdminSessionState.data?.admin.user_details.company.company_id === 1
      ) {
        return false;
      }

      return (
        currentInventory?.find(
          (invItem) => invItem.productId === orderItem.productId
        )?.currentStock === 0
      );
    });

    dispatch(
      openMessageModal({
        message: withOutOfStock
          ? "Are you sure you want to place this order with a product that is marked as out of stock?"
          : "Are you sure you want to place this order?",
        buttons: [
          {
            color: "#CC5801",
            text: "Yes",
            onClick: () => {
              const mainOrderItems = orderProductData?.filter((orderItem) => {
                if (
                  getAdminSessionState.data?.admin.user_details.company
                    .company_id === 1
                ) {
                  return orderItem;
                }

                return (
                  currentInventory?.find(
                    (invItem) => invItem.productId === orderItem.productId
                  )?.currentStock !== 0
                );
              });

              const subOrderItems = orderProductData?.filter(
                (orderItem) =>
                  currentInventory?.find(
                    (invItem) => invItem.productId === orderItem.productId
                  )?.currentStock === 0
              );

              dispatch(
                insertNewOrder({
                  selectedStoreId:
                    data?.selectedStoreId === ""
                      ? undefined
                      : data?.selectedStoreId,
                  deliveryScheduleData:
                    deliveryDate === "" || deliveryDate === null
                      ? undefined
                      : deliveryDate,
                  selectedAddress: data?.selectedAddress ?? "",
                  remarks: remarks === "" ? undefined : remarks,
                  remarksFromOrderOverride:
                    overrideStatus === 0 ? undefined : overrideStatus,
                  category:
                    data?.category?.category_id === ""
                      ? undefined
                      : (data?.category as categoryModel),
                  fulfillment:
                    data?.fulfillment?.fulfillment_id === ""
                      ? undefined
                      : (data?.fulfillment as fulfillmentModel),
                  totalCost: totalAmount?.toFixed(2),
                  OrderData: data?.isStaggered ? undefined : mainOrderItems,
                  batchData: batch ?? [],
                  selectedSupplier: data?.selectedSupplier ?? undefined,
                  companyId:
                    getAdminSessionState.data?.admin.user_details.company
                      .company_id,
                  poNumber: data?.poNumber ?? undefined,
                  uploadedPo: data?.uploadedPo ?? undefined,
                  withOutOfStock: withOutOfStock ? withOutOfStock : undefined,
                  outOfStockProduct:
                    subOrderItems.length === 0 ||
                    getAdminSessionState.data?.admin.user_details.company
                      .company_id === 1
                      ? undefined
                      : subOrderItems,

                  testMode: test ? test : undefined,
                })
              );

              //  Cleaning

              dispatch(closeMessageModal());
              document.body.classList.remove("overflow-hidden");
              props.onClose();
              resetState();
            },
          },
          {
            color: "#22201A",
            text: "No",
            onClick: () => {
              dispatch(closeMessageModal());
            },
          },
        ],
      })
    );
  };

  const resetState = () => {
    setOrderProductData([]);
    setBatches([]);
    setRemarks("");
    setIsChecked(false);
    setIsOrderOverride(false);
    setTest(false);
    setDeliveryDate(null);
    dispatch(resetNewOrder());
  };

  const overrideStatus = !(
    Number(getMinimumTotalAmountState.data?.totalPrice) >= totalAmount
  )
    ? 0
    : isOrderOverride === false
    ? 0
    : 1;

  /**
   * Handles the override change
   */
  const handleOverrideChange = () => {
    if (!isChecked) {
      setIsChecked(!isChecked);
      setIsOrderOverride(true);
      dispatch(closeMessageModal());
    } else {
      setIsChecked(!isChecked);
      setIsOrderOverride(false);
      dispatch(closeMessageModal());
    }
  };

  /**
   * handles the disable of place order button
   * @returns boolean
   */
  const placeOrderDisable = () => {
    const belowMinimum =
      Number(getMinimumTotalAmountState.data?.totalPrice) >= totalAmount;

    const isQuantityEmpty = orderProductData?.some(
      (prodItem) => prodItem.orderQty === 0 || prodItem.orderQty === undefined
    );

    return (
      (isChecked && remarks === "" && belowMinimum) ||
      (isChecked === false && belowMinimum) ||
      deliveryDate === null ||
      isQuantityEmpty ||
      orderProductData.length === 0
    );
  };

  /**
   * Display current supplier
   */
  const previewSelectedSupplier =
    adminSettingsEditUserMaterialSupplierCustomerState.data &&
    adminSettingsEditUserMaterialSupplierCustomerState.data?.supplier?.find(
      (supp) => supp.id === getOrderInformation.data?.selectedSupplier
    );

  /**
   * Display store name or company name
   */

  const companyStoreName = () => {
    const storeName = getStores.data?.stores.find((store) => {
      return store.store_id === getOrderInformation.data?.selectedStoreId;
    })?.name;

    const companyName =
      getAdminSessionState.data?.admin.user_details.company.name;

    return data?.selectedStoreId ? storeName : companyName;
  };

  const handleBatchChange = useCallback(
    (batches: BatchModel[], totalCost: number) => {
      setBatches(batches);
      setTotalAmount(totalCost);
    },
    []
  );

  const handleSelectedProduct = (selectedProduct: StockProductItem[]) => {
    const updatedProd: OrderTableData[] = selectedProduct
      .filter(
        (prod) =>
          !orderProductData.find(
            (prodData) => prodData.productId === prod.product_id
          )
      )
      .map((prod) => ({
        productId: prod.product_id,
        productName: prod.product_name,
        uom: prod.uom,
        orderQty: 1,
        cost: prod.cost,
        itemTotalCost: prod.cost * prod.orderQty,
        qty_per_order: prod.uom_multiplier,
        uom_multiplier: prod.uom_multiplier,
        limit: prod.limit,
      }));

    setOrderProductData((prev) => [...prev, ...updatedProd]);

    if (data?.selectedSupplier) {
      const params = new URLSearchParams();
      [
        ...orderProductData.map((p) => p.productId),
        ...selectedProduct.map((p) => p.product_id),
      ].forEach((id) => params.append("prodId[]", id));

      dispatch(
        stockInventoryFetchCurrentStock({
          query: params.toString(),
          supplierId: data?.selectedSupplier,
        })
      );
    }
  };

  const handleOnRemove = (id: string) => () => {
    setOrderProductData((prev) => prev.filter((prod) => prod.productId !== id));
  };

  /**
   * Determines whether a given date should be disabled
   * based on business lead-time rules.
   *
   * Lead-time rules (relative to today's date):
   * - If today is Friday   → minimum lead time is 5 days (earliest next Wednesday)
   * - If today is Saturday → minimum lead time is 4 days (earliest next Wednesday)
   * - If today is Sunday   → all dates disabled (not selectable)
   * - If today is Monday–Thursday → minimum lead time is 3 days
   *
   * Example:
   *  - Today = Tuesday → lead time = 3 → earliest selectable = Friday → Saturday is valid
   *
   * @param {Date} date - The candidate date to check for availability.
   * @param {Date} [today=new Date()] - The base date from which lead time is calculated.
   *                                    Defaults to the current system date.
   * @returns {boolean} True if the date should be disabled (not selectable), false otherwise.
   */
  const shouldDisableDate = (date: Date, today: Date = new Date()): boolean => {
    const LEAD_TIME_RULES: Record<number, number | null> = {
      0: null, // Sunday → never selectable
      1: 3, // Monday
      2: 3, // Tuesday
      3: 3, // Wednesday
      4: 3, // Thursday
      5: 5, // Friday
      6: 4, // Saturday
    };

    const day = today.getDay();
    const leadTime = LEAD_TIME_RULES[day];

    // Block all Sundays
    if (leadTime === null) return true;

    // Calculate earliest allowed date
    const earliestAllowed = new Date(today);
    earliestAllowed.setDate(today.getDate() + leadTime);

    return date < earliestAllowed || date.getDay() === 0;
  };

  if (props.open) {
    document.body.classList.add("overflow-hidden");
  } else {
    document.body.classList.remove("overflow-hidden");
    return null;
  }
  return (
    <>
      <div className="fixed inset-0 z-10 flex items-start justify-center overflow-auto bg-black/60 backdrop-blur-sm">
        <div className="w-[97%] max-w-6xl my-8 bg-white rounded-2xl shadow-2xl border border-gray-200">
          {/* Modern Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-700 rounded-t-2xl flex items-center justify-between p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Package className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white">
                Order Confirmation
              </h1>
            </div>
            <button
              className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
              onClick={() => {
                resetState();
                document.body.classList.remove("overflow-hidden");
                props.onClose();
              }}
            >
              <IoMdClose className="h-6 w-6 text-white" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Back Button */}
            <button
              onClick={() => {
                setOrderProductData([]);
                document.body.classList.remove("overflow-hidden");
                props.onClose();
                props?.openPlaceOrderModal && props?.openPlaceOrderModal(true);
              }}
              className="group flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors duration-200"
            >
              <IoArrowBackCircleOutline className="h-5 w-5" />
              <span className="group-hover:underline underline-offset-2">
                Back to product selection
              </span>
            </button>

            {/* Order Summary Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-200">
                <div className="flex items-center gap-2 mb-1">
                  <Building className="h-4 w-4 text-blue-600" />
                  <span className="font-semibold text-blue-900 text-sm">
                    Store Information
                  </span>
                </div>
                <p className="text-xs font-medium text-blue-800 uppercase">
                  {companyStoreName() ?? "Problem encountered"}
                </p>
                <p className="text-xs text-blue-600 capitalize">
                  {data?.selectedAddress ?? "Problem encountered"}
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-3 border border-green-200">
                <div className="flex items-center gap-2 mb-1">
                  <Package className="h-4 w-4 text-green-600" />
                  <span className="font-semibold text-green-900 text-sm">
                    Supplier
                  </span>
                </div>
                <p className="text-xs font-medium text-green-800 uppercase">
                  {previewSelectedSupplier?.name ?? "Problem encountered"}
                </p>
              </div>

              {getAdminSessionState.data?.admin.user_details.company
                .company_id === 2 && (
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-3 border border-orange-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Boxes className="h-4 w-4 text-orange-600" />
                    <span className="font-semibold text-orange-900 text-sm">
                      Category
                    </span>
                  </div>
                  <p className="text-xs font-medium text-orange-800 uppercase">
                    {data?.category?.category_name ?? "Problem encountered"}
                  </p>
                </div>
              )}

              {getAdminSessionState.data?.admin.user_details.company
                .company_id === 1 && (
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-3 border border-orange-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Truck className="h-4 w-4 text-orange-600" />
                    <span className="font-semibold text-orange-900 text-sm">
                      Fulfillment Method
                    </span>
                  </div>
                  <p className="text-xs font-medium text-orange-800 uppercase">
                    {data?.fulfillment?.fulfillment_name ??
                      "Problem encountered"}
                  </p>
                </div>
              )}

              {!data?.isStaggered && (
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3 border border-purple-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="h-4 w-4 text-purple-600" />
                    <span className="font-semibold text-purple-900 text-sm">
                      Delivery Date
                    </span>
                  </div>
                  <p className="text-xs font-medium text-purple-800 uppercase">
                    {(deliveryDate && dateSetup(deliveryDate, false)) ??
                      "To be determined"}
                  </p>
                </div>
              )}
            </div>

            {/* Out of Stock Warning */}
            <BannerIndicator
              visible={currentInventory?.some(
                (prod) => prod.currentStock === 0
              )}
              textColor="text-amber-800"
              bgColor="bg-amber-50 border border-amber-200 rounded-xl"
              content="Some items you're ordering are out of stock. Since
                      multiple stores share inventory, stock levels can change
                      quickly."
            />

            {getOrderInformation.data?.isStaggered ? (
              <ConfirmOrderBatchProductCreator
                selectedProducts={orderProductData.map((product) => ({
                  ...product,
                  orderQty: product.orderQty || 1,
                  cost: Number(product.cost),
                  itemTotalCost: (product.orderQty || 1) * Number(product.cost),
                }))}
                onBatchesChange={handleBatchChange}
                onConfirm={handleSubmit}
              />
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 sm:px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Order Items
                  </h3>
                </div>

                {/* Desktop Table View */}
                <div className="hidden md:block">
                  <TableContainer component={Paper} className="shadow-none">
                    <Table size="small">
                      <TableHead>
                        <TableRow className="bg-gray-50">
                          {columns.map((row, index) => (
                            <TableCell
                              variant="head"
                              align={
                                index < 2 || index === 6 ? "left" : "right"
                              }
                              key={index}
                              className="font-semibold text-gray-900 border-b border-gray-200"
                            >
                              <span className="text-gray-900 text-sm font-semibold">
                                {row.label}
                              </span>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {orderProductData?.map((product, index: number) => {
                          return (
                            <StockOrderConfirmTable
                              key={product.productId}
                              orderProductData={product}
                              setOrderProductData={(data) =>
                                handleProductChange(index, data)
                              }
                              stockCount={
                                currentInventory?.find(
                                  (prod) => prod.productId === product.productId
                                )?.currentStock ?? 0
                              }
                              onRemove={handleOnRemove}
                              companyId={
                                getAdminSessionState.data?.admin.user_details
                                  .company.company_id
                              }
                            />
                          );
                        })}

                        <TableRow>
                          <TableCell
                            onClick={() => setOpenAddProduct(true)}
                            className="cursor-pointer group relative overflow-hidden transition-all duration-200 ease-out hover:shadow-md"
                            /**
                             * Set colspan to 8 when multim is ordering.
                             */
                            colSpan={isMultimOrdering ? 7 : 8}
                            align="center"
                          >
                            <div className="flex items-center justify-center gap-3 py-6 px-4 rounded-lg border-2 border-dashed border-gray-300 group-hover:border-blue-500 group-hover:bg-blue-50 transition-all duration-200">
                              <div className="p-2 bg-gray-100 group-hover:bg-blue-500 rounded-lg transition-colors duration-200">
                                <Plus className="h-5 w-5 text-gray-600 group-hover:text-white" />
                              </div>
                              <span className="text-gray-700 font-medium group-hover:text-blue-600 transition-colors duration-200">
                                Add More Products
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                      <TableFooter>
                        <TableRow className="bg-gradient-to-r from-blue-50 to-purple-50">
                          <TableCell
                            className={`font-semibold ${
                              !data?.category?.category_name && "hidden"
                            }`}
                          >
                            {data?.category?.category_name}
                          </TableCell>
                          <TableCell
                            align="right"
                            /**
                             * Set colspan to 5 when multim is ordering
                             */
                            colSpan={isMultimOrdering ? 4 : 5}
                            className="font-semibold text-gray-900"
                          >
                            Total Order Value:
                          </TableCell>
                          <TableCell
                            align="right"
                            className="font-bold text-xl text-blue-600"
                          >
                            ₱{moneyFormatter(totalAmount, 2)}
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>
                </div>

                {/* Mobile Card/List View */}
                <div className="block md:hidden">
                  <div className="divide-y divide-gray-200">
                    {orderProductData?.map((product, index: number) => (
                      <MobileStockOrderCard
                        key={product.productId}
                        orderProductData={product}
                        setOrderProductData={(data) =>
                          handleProductChange(index, data)
                        }
                        stockCount={
                          currentInventory?.find(
                            (prod) => prod.productId === product.productId
                          )?.currentStock ?? 0
                        }
                        onRemove={handleOnRemove}
                        companyId={
                          getAdminSessionState.data?.admin.user_details.company
                            .company_id
                        }
                      />
                    ))}

                    {/* Add More Products Card */}
                    <div
                      onClick={() => setOpenAddProduct(true)}
                      className="p-4 cursor-pointer group hover:bg-blue-50 transition-all duration-200"
                    >
                      <div className="flex items-center justify-center gap-3 py-6 px-4 rounded-lg border-2 border-dashed border-gray-300 group-hover:border-blue-500 transition-all duration-200">
                        <div className="p-2 bg-gray-100 group-hover:bg-blue-500 rounded-lg transition-colors duration-200">
                          <Plus className="h-5 w-5 text-gray-600 group-hover:text-white" />
                        </div>
                        <span className="text-gray-700 font-medium group-hover:text-blue-600 transition-colors duration-200">
                          Add More Products
                        </span>
                      </div>
                    </div>

                    {/* Mobile Total */}
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4">
                      <div className="flex justify-between items-center">
                        {data?.category?.category_name && (
                          <span className="font-semibold text-gray-900">
                            {data.category.category_name}
                          </span>
                        )}
                        <div className="text-right">
                          <div className="text-sm font-semibold text-gray-900">
                            Total Order Value:
                          </div>
                          <div className="font-bold text-xl text-blue-600">
                            ₱{moneyFormatter(totalAmount, 2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {!getOrderInformation.data?.isStaggered && (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Delivery Date Section */}
                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <Calendar className="h-5 w-5 text-blue-600" />
                        <span className="font-semibold text-gray-900">
                          Delivery Date
                        </span>
                      </div>
                      <button
                        className="text-sm text-blue-600 hover:text-blue-800 underline underline-offset-2 transition-colors duration-200"
                        onClick={() => setDeliveryDate(null)}
                      >
                        Reset
                      </button>
                    </div>
                    <MaterialDateInput
                      value={deliveryDate}
                      size="small"
                      inputValue={
                        deliveryDate &&
                        dayjs(deliveryDate).format("MMMM DD, YYYY")
                      }
                      placeholder="Select requested delivery date"
                      readonly
                      openTo="day"
                      views={["month", "day"]}
                      required
                      colorTheme="black"
                      onChange={handleRequestedDeliveryDate}
                      minDate={dayjs().add(2, "day").toDate()}
                      shouldDisableDate={(date) => {
                        return date.getDay() === 0;
                      }}
                    />
                  </div>

                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <div className="space-y-6">
                      {/* Test Mode */}
                      {getAdminSessionState.data?.admin.user_details
                        .testMode && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={test}
                                  onChange={(event, check) => setTest(check)}
                                />
                              }
                              label={
                                <span className="font-medium text-gray-900">
                                  Test Mode
                                </span>
                              }
                            />
                          </div>
                          <p className="text-sm text-gray-600 ml-8">
                            Enable test mode to simulate order placement without
                            affecting actual inventory or creating real orders.
                          </p>
                        </div>
                      )}

                      {/* Override Controls - Always Visible */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <label
                                className={`flex items-center gap-3 cursor-pointer ${
                                  !(
                                    Number(
                                      getMinimumTotalAmountState.data
                                        ?.totalPrice
                                    ) >= totalAmount
                                  )
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={handleOverrideChange}
                                  disabled={
                                    !(
                                      Number(
                                        getMinimumTotalAmountState.data
                                          ?.totalPrice
                                      ) >= totalAmount
                                    )
                                  }
                                  className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                />
                                <span className="font-medium text-gray-900">
                                  Order Override
                                </span>
                              </label>
                            </div>
                            <div className="ml-8 mt-1">
                              <p className="text-sm text-gray-600">
                                {Number(
                                  getMinimumTotalAmountState.data?.totalPrice
                                ) >= totalAmount ? (
                                  <>
                                    Your order total (₱
                                    {moneyFormatter(totalAmount, 2)}) is below
                                    the minimum amount of ₱
                                    {moneyFormatter(
                                      getMinimumTotalAmountState.data
                                        ?.totalPrice,
                                      2
                                    )}
                                    . Enable override to proceed with this
                                    order.
                                  </>
                                ) : (
                                  <>
                                    Allow placing orders below the minimum
                                    threshold of ₱
                                    {moneyFormatter(
                                      getMinimumTotalAmountState.data
                                        ?.totalPrice,
                                      2
                                    )}
                                    . Override is currently not needed as your
                                    order meets the minimum requirement.
                                  </>
                                )}
                              </p>
                            </div>
                          </div>

                          {/* Override Status Badge */}
                          <div className="ml-4">
                            {Number(
                              getMinimumTotalAmountState.data?.totalPrice
                            ) >= totalAmount ? (
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                Override Required
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Minimum Met
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Override Reason Input */}
                        {isChecked && (
                          <div className="ml-8 mt-3">
                            <MaterialInput
                              label="Reason for override"
                              placeholder="Please provide a reason for overriding the minimum order amount..."
                              colorTheme="black"
                              name="remarks"
                              size="small"
                              value={remarks}
                              multiline
                              fullWidth
                              onChange={(e) => setRemarks(e.target.value)}
                              helperText="A detailed reason is required when overriding minimum order requirements."
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end pt-4">
                  <button
                    onClick={handleSubmit}
                    disabled={placeOrderDisable()}
                    className={`px-8 py-4 rounded-xl font-semibold text-white transition-all duration-200 ${
                      placeOrderDisable()
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-gradient-to-r from-blue-600 to-purple-700 hover:from-blue-700 hover:to-purple-800 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    }`}
                  >
                    Place Order
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <AddProductSelectModal
        isOpen={openAddProduct}
        onClose={() => setOpenAddProduct(false)}
        onConfirm={handleSelectedProduct}
        products={
          getProductData.data?.products.filter(
            (prod) =>
              orderProductData?.find(
                (orderProd) => orderProd.productId === prod.product_id
              )?.productId !== prod.product_id
          ) ?? []
        }
      />
    </>
  );
}

const StockOrderConfirmTable = React.memo(function StockOrderConfirmTable({
  orderProductData,
  setOrderProductData,
  stockCount,
  onRemove,
  companyId,
}: {
  orderProductData: OrderTableData;
  setOrderProductData: (ordrData: OrderTableData) => void;
  stockCount: number;
  onRemove: (id: string) => () => void;
  companyId: number | undefined;
}) {
  const {
    productId,
    productName,
    uom,
    cost,
    qty_per_order,
    uom_multiplier,
    itemTotalCost,
    orderQty,
    limit,
  } = orderProductData;

  const [quantity, setQuantity] = useState(orderQty ?? 0);
  const currentStock = stockCount - quantity;

  const handleQuantityChange = (newQty: number) => {
    setQuantity(newQty);

    setOrderProductData({
      ...orderProductData,
      orderQty: newQty,
      qty_per_order: newQty * Number(uom_multiplier),
      itemTotalCost: newQty * cost,
    });
  };

  return (
    <TableRow
      className={`transition-colors duration-200 hover:bg-gray-50 ${
        quantity === 0 ? "bg-red-50 border-l-4 border-red-400" : ""
      }`}
    >
      <TableCell className="font-medium text-gray-900">{productId}</TableCell>
      <TableCell className="font-medium text-gray-900">{productName}</TableCell>
      <TableCell align="right" className="font-medium">
        ₱{moneyFormatter(cost, 2)}
      </TableCell>
      {companyId !== 1 && (
        <TableCell>
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              stockCount <= 0
                ? "bg-red-100 text-red-800"
                : currentStock > 10
                ? "bg-green-100 text-green-800"
                : "bg-yellow-100 text-yellow-800"
            }`}
          >
            {stockCount <= 0 ? "Out of stock" : currentStock}
          </span>
        </TableCell>
      )}
      <TableCell>
        <StockOrderHandleQuantity
          setNewCurrentValue={handleQuantityChange}
          currentValue={quantity}
          maxQuantity={
            companyId === 1
              ? // stockCount.toString().length > limit
                Math.pow(10, limit) - 1
              : stockCount
            // companyId === 1 ? limit : stockCount === 0 ? 9999 : stockCount
          }
        />
      </TableCell>
      <TableCell className="text-gray-600">{uom}</TableCell>

      <TableCell align="right" className="font-bold text-blue-600">
        ₱{moneyFormatter(itemTotalCost, 2)}
      </TableCell>
      <TableCell>
        <button
          onClick={onRemove(productId)}
          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-200"
        >
          <LuTrash2 className="h-4 w-4" />
        </button>
      </TableCell>
    </TableRow>
  );
});

const MobileStockOrderCard = React.memo(function MobileStockOrderCard({
  orderProductData,
  setOrderProductData,
  stockCount,
  onRemove,
  companyId,
}: {
  orderProductData: OrderTableData;
  setOrderProductData: (ordrData: OrderTableData) => void;
  stockCount: number;
  onRemove: (id: string) => () => void;
  companyId: number | undefined;
}) {
  const {
    productId,
    productName,
    uom,
    cost,
    qty_per_order,
    uom_multiplier,
    itemTotalCost,
    orderQty,
  } = orderProductData;

  const [quantity, setQuantity] = useState(orderQty ?? 0);
  const currentStock = stockCount - quantity;

  const handleQuantityChange = (newQty: number) => {
    setQuantity(newQty);
    setOrderProductData({
      ...orderProductData,
      orderQty: newQty,
      qty_per_order: newQty * Number(uom_multiplier),
      itemTotalCost: newQty * cost,
    });
  };

  return (
    <div
      className={`p-4 ${
        quantity === 0 ? "bg-red-50 border-l-4 border-red-400" : ""
      }`}
    >
      {/* Product Header */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <div className="font-medium text-gray-900 text-sm">{productName}</div>
          <div className="text-xs text-gray-500 mt-1">ID: {productId}</div>
        </div>
        <button
          onClick={onRemove(productId)}
          className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-200 ml-2"
        >
          <LuTrash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Product Details Grid */}
      <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
        <div>
          <span className="text-gray-500">UOM:</span>
          <span className="ml-2 font-medium">{uom}</span>
        </div>
        <div>
          <span className="text-gray-500">Unit Cost:</span>
          <span className="ml-2 font-medium">₱{moneyFormatter(cost, 2)}</span>
        </div>
        {companyId !== 1 && (
          <div>
            <span className="text-gray-500">Stock:</span>
            <span className="ml-2">
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  stockCount <= 0
                    ? "bg-red-100 text-red-800"
                    : currentStock > 10
                    ? "bg-green-100 text-green-800"
                    : "bg-yellow-100 text-yellow-800"
                }`}
              >
                {stockCount <= 0 ? "Out of stock" : currentStock}
              </span>
            </span>
          </div>
        )}
        <div>
          <span className="text-gray-500">Total Units:</span>
          <span className="ml-2 font-medium">{qty_per_order}</span>
        </div>
      </div>

      {/* Quantity Control and Total */}
      <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-700">Quantity:</span>
          <StockOrderHandleQuantity
            setNewCurrentValue={handleQuantityChange}
            currentValue={quantity}
            maxQuantity={
              companyId === 1 ? 99999999 : stockCount === 0 ? 9999 : stockCount
            }
          />
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-500">Total Cost</div>
          <div className="font-bold text-blue-600">
            ₱{moneyFormatter(itemTotalCost, 2)}
          </div>
        </div>
      </div>
    </div>
  );
});
